import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useSetRecoilState } from "recoil";
import { currentPostAtom } from "../atom";

import { getLoginUser } from "../api/user";
import { getCategoryInfo } from "../api/categoryAPI";
import { getPostList, createPost, getOcspstLinkedPosts } from "../api/postAPI";
import { OCSPST_CATEGORY } from "../component/category/ocspstCategory";

import styled from "styled-components";
import PostMediaCounts from "../component/category/PostMediaCounts";
import { Spin, Empty, Layout, Result, Button } from "antd";
import { PlusOutlined, LockOutlined, DownOutlined } from "@ant-design/icons";
import { SubColor } from "../style/styleValue";
import { ACCESS_TOKEN } from "../lib/cookie";

const Category = ({ rootCategoryId = 600 }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [mainCategories, setMainCategories] = useState<any[]>([]);
  const [activeMainId, setActiveMainId] = useState<number | null>(null);
  const [subDataMap, setSubDataMap] = useState<Record<number, any[]>>({});
  const [loading, setLoading] = useState(true);
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [contentLoading, setContentLoading] = useState(false);
  const [expandedSubs, setExpandedSubs] = useState<Record<number, boolean>>({});
  const [searchParams, setSearchParams] = useSearchParams();
  // OCSPST 가상 카테고리: 서브 없이 링크 걸린 게시글 한 목록 (null = 로딩 전)
  const [ocspstPosts, setOcspstPosts] = useState<any[] | null>(null);
  const isOcspstActive = searchParams.get("category") === OCSPST_CATEGORY.key;

  const setCurrentPost = useSetRecoilState(currentPostAtom);
  const navigate = useNavigate();

  const handleMainClick = (mainId: number | string, mainName: string) => {
    setSearchParams({ category: String(mainId), categoryName: mainName });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const fetchSubCategories = async (mainId: number) => {
    setActiveMainId(mainId);
    if (subDataMap[mainId]) return;

    // 하위 카테고리는 최초 getCategoryInfo(rootCategoryId) 호출로 이미 트리에 들어와 있으므로
    // 재조회하지 않고 mainCategories 에서 꺼내 쓴다. (게시글 목록만 별도 조회)
    const main = mainCategories.find((m) => m.id === mainId);
    const subs = main?.children ? [...main.children] : [];
    if (subs.length === 0) {
      setSubDataMap((prev) => ({ ...prev, [mainId]: [] }));
      return;
    }

    setContentLoading(true);
    try {
      subs.sort((a: any, b: any) => a.listOrder - b.listOrder);
      const subWithPosts = await Promise.all(
        subs.map(async (sub: any) => {
          const postListData = await getPostList(1, 9999, sub.id, "id", "DESC");
          if (postListData.length > 0) {
            postListData.sort((a: any, b: any) => a.listOrder - b.listOrder);
          }
          return { ...sub, posts: postListData };
        })
      );
      setSubDataMap((prev) => ({ ...prev, [mainId]: subWithPosts }));
    } catch (error) {
      console.error(error);
    } finally {
      setContentLoading(false);
    }
  };

  const toggleSub = (subId: number) => {
    setExpandedSubs((prev) => ({ ...prev, [subId]: !prev[subId] }));
  };

  // 마우스를 올리면 펼친다. 마우스가 벗어나도 접지 않고 유지 (접기는 클릭으로)
  const expandSub = (subId: number) => {
    setExpandedSubs((prev) => (prev[subId] ? prev : { ...prev, [subId]: true }));
  };

  // 서브 카테고리 섹션의 DOM 참조 (breadcrumb 등에서 특정 서브로 스크롤하기 위함)
  const subSectionRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  // 동일 진입에서 자동 펼침/스크롤을 한 번만 수행하기 위한 처리 키
  const handledSubScrollRef = useRef<string | null>(null);

  const handleAddPost = async (subId: number, subName: string) => {
    const postId = await createPost(Number(subId));
    setCurrentPost({
      id: postId, categoryId: Number(subId), title: "", writer: "", slides: [],
      isActive: false, createdAt: "", modifiedAt: "", content: "", postThumbnail: "", comments: [], isSecret: false,
    });
    const params = new URLSearchParams();
    params.set("from", "portal");
    const categoryId = searchParams.get("category");
    const categoryName = searchParams.get("categoryName");
    if (categoryId) params.set("category", categoryId);
    if (categoryName) params.set("categoryName", categoryName);
    params.set("subCategoryName", subName);
    navigate(`/write?${params.toString()}`);
  };

  useEffect(() => {
    const fetchMain = async () => {
      setLoading(true);
      try {
        const res = await getCategoryInfo(rootCategoryId);
        const data = res.children;
        if (data && data.length > 0) {
          data.sort((a: any, b: any) => a.listOrder - b.listOrder);
          setMainCategories(data);
          const currentParam = searchParams.get("category");
          if (!currentParam) {
            setSearchParams({ category: String(data[0].id), categoryName: data[0].name }, { replace: true });
          }
        }
      } catch (error) {
        console.error(error);
        setIsUnauthorized(true);
      } finally {
        setLoading(false);
      }
    };
    fetchMain();
  }, [rootCategoryId]);

  useEffect(() => {
    if (mainCategories.length === 0) return;
    const categoryParam = searchParams.get("category");
    if (categoryParam === OCSPST_CATEGORY.key) {
      setActiveMainId(null);
    } else if (categoryParam) {
      fetchSubCategories(Number(categoryParam));
    } else {
      fetchSubCategories(mainCategories[0].id);
    }
  }, [searchParams, mainCategories]);

  // breadcrumb에서 subCategoryName 을 달고 진입하면, 해당 서브 카테고리를 펼치고 그 위치로 스크롤한다.
  useEffect(() => {
    const subCategoryName = searchParams.get("subCategoryName");
    if (!subCategoryName || activeMainId == null) return;

    const subs = subDataMap[activeMainId];
    if (!subs) return; // 아직 로딩 전

    const target = subs.find((s) => s.name === subCategoryName);
    if (!target) return;

    const key = `${activeMainId}:${subCategoryName}`;
    if (handledSubScrollRef.current === key) return; // 이미 처리한 진입이면 스킵 (사용자 조작 방해 X)
    handledSubScrollRef.current = key;

    setExpandedSubs((prev) => ({ ...prev, [target.id]: true }));
    // 펼침 반영 후 다음 프레임에 스크롤
    requestAnimationFrame(() => {
      subSectionRefs.current.get(target.id)?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [subDataMap, activeMainId, searchParams]);

  // 메뉴 건수 표시에도 쓰이므로 선택 여부와 상관없이 처음에 한 번 불러온다.
  useEffect(() => {
    getOcspstLinkedPosts(1, 9999, "id", "DESC").then(setOcspstPosts);
  }, []);

  useEffect(() => {
    if (!localStorage.getItem(ACCESS_TOKEN)) navigate("/login");
    getLoginUser().then((u) => setIsAdmin(u?.role === "ADMIN_MAIN")).catch(() => setIsAdmin(false));
  }, []);

  if (loading) return <Center><Spin size="large" /></Center>;

  if (isUnauthorized) {
    return (
      <Layout.Content style={{ minHeight: "80vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Result
          status="403"
          title="접근 권한 없음"
          subTitle="해당 게시판에 접근 권한이 없습니다."
          extra={
            <Button type="primary" onClick={() => navigate("/")}>
              메인으로 돌아가기
            </Button>
          }
        />
      </Layout.Content>
    );
  }

  // 게시글 수(postCnt) + 슬라이드 수(slideCnt)를 나란히 표기
  const renderCounts = (postCnt?: number, slideCnt?: number) => (
    <>
      <span className="cnt">Post: <b>{postCnt ?? 0}</b></span>
      <span className="cnt">Image: <b>{slideCnt ?? 0}</b></span>
    </>
  );

  const openOcspstPost = (post: any) => {
    const params = new URLSearchParams();
    params.set("title", post.title || "제목 없음");
    params.set("from", "portal");
    params.set("category", OCSPST_CATEGORY.key);
    params.set("categoryName", OCSPST_CATEGORY.name);
    navigate(`/post/${post.id}?${params.toString()}`);
  };

  const ocspstSlideCnt = (ocspstPosts ?? []).reduce((sum, p) => sum + (p.slideCnt ?? 0), 0);

  return (
    <StCategoryContainer>
      <HalfLayout>
        <SideMenu>
          <MenuList>
            {mainCategories.map((main) => (
              <MainMenuItem
                key={main.id}
                $active={activeMainId === main.id}
                onClick={() => handleMainClick(main.id, main.name)}
              >
                <MenuItemLabel $active={activeMainId === main.id}>
                  {main.name}
                </MenuItemLabel>
                <CountBadge $active={activeMainId === main.id}>
                  {renderCounts(main.postCnt, main.slideCnt)}
                </CountBadge>
              </MainMenuItem>
            ))}
            <MainMenuItem
              $active={isOcspstActive}
              onClick={() => handleMainClick(OCSPST_CATEGORY.key, OCSPST_CATEGORY.name)}
            >
              <MenuItemLabel $active={isOcspstActive}>{OCSPST_CATEGORY.name}</MenuItemLabel>
              <CountBadge $active={isOcspstActive}>
                {renderCounts(ocspstPosts?.length, ocspstSlideCnt)}
              </CountBadge>
            </MainMenuItem>
          </MenuList>
        </SideMenu>

        <ContentArea>
          {isOcspstActive ? (
            ocspstPosts === null ? (
              <Center><Spin tip="Loading contents..." /></Center>
            ) : ocspstPosts.length > 0 ? (
              <SubSection>
                <SubHeader style={{ cursor: "default" }}>
                  <SubHeaderLeft>
                    <SubTitle>{OCSPST_CATEGORY.name}</SubTitle>
                    <SubCount>{renderCounts(ocspstPosts.length, ocspstSlideCnt)}</SubCount>
                  </SubHeaderLeft>
                </SubHeader>
                <PostList>
                  {ocspstPosts.map((post) => (
                    <PostLink key={post.id} onClick={() => openOcspstPost(post)}>
                      {post.isSecret && (
                        <LockOutlined style={{ marginRight: "6px", color: "#bfbfbf" }} />
                      )}
                      {post.title || "제목 없음"}
                      <PostMediaCounts post={post} />
                    </PostLink>
                  ))}
                </PostList>
              </SubSection>
            ) : (
              <Empty style={{ marginTop: 100 }} description="연결된 게시글이 없습니다." />
            )
          ) : contentLoading ? (
            <Center><Spin tip="Loading contents..." /></Center>
          ) : activeMainId && subDataMap[activeMainId] ? (
            subDataMap[activeMainId].length > 0 ? (
              subDataMap[activeMainId].map((sub) => {
                const isExpanded = !!expandedSubs[sub.id];
                return (
                  <SubSection
                    key={sub.id}
                    ref={(el: HTMLDivElement | null) => {
                      if (el) subSectionRefs.current.set(sub.id, el);
                      else subSectionRefs.current.delete(sub.id);
                    }}
                  >
                    <SubHeader
                      onMouseEnter={() => expandSub(sub.id)}
                      onClick={() => toggleSub(sub.id)}
                    >
                      <SubHeaderLeft>
                        <ChevronIcon $expanded={isExpanded}>
                          <DownOutlined />
                        </ChevronIcon>
                        <SubTitle>{sub.name}</SubTitle>
                        <SubCount>{renderCounts(sub.postCnt, sub.slideCnt)}</SubCount>
                      </SubHeaderLeft>
                      {isAdmin && (
                        <StAddButton
                          onClick={(e) => { e.stopPropagation(); handleAddPost(sub.id, sub.name); }}
                        >
                          <PlusOutlined style={{ fontSize: "12px" }} /> Add Post
                        </StAddButton>
                      )}
                    </SubHeader>

                    <PostListWrapper $expanded={isExpanded} $count={sub.posts.length}>
                      <PostList>
                        {sub.posts.map((post: any) => (
                          <PostLink
                            key={post.id}
                            onClick={() => {
                              // 뒤로가기 시 이 서브가 펼쳐진 위치로 복원되도록 portal URL 에도 subCategoryName 을 남긴다.
                              const restoreParams = new URLSearchParams(searchParams);
                              restoreParams.set("subCategoryName", sub.name);
                              setSearchParams(restoreParams, { replace: true });

                              const params = new URLSearchParams();
                              params.set("title", post.title || post.name || "제목 없음");
                              params.set("from", "portal");
                              const categoryId = searchParams.get("category");
                              const categoryName = searchParams.get("categoryName");
                              if (categoryId) params.set("category", categoryId);
                              if (categoryName) params.set("categoryName", categoryName);
                              params.set("subCategoryName", sub.name);
                              navigate(`/post/${post.id}?${params.toString()}`);
                            }}
                          >
                            {post.isSecret && (
                              <LockOutlined style={{ marginRight: "6px", color: "#bfbfbf" }} />
                            )}
                            {post.title || post.name || "제목 없음"}
                            <PostMediaCounts post={post} />
                          </PostLink>
                        ))}
                      </PostList>
                    </PostListWrapper>
                  </SubSection>
                );
              })
            ) : (
              <Empty style={{ marginTop: 100 }} description="하위 항목이 없습니다." />
            )
          ) : null}
        </ContentArea>
      </HalfLayout>
    </StCategoryContainer>
  );
};

const StCategoryContainer = styled.div`
  width: 95%;
  max-width: 1600px;
  margin: 3rem auto;
`;

const HalfLayout = styled.div`
  display: flex;
  gap: 48px;
  align-items: flex-start;
`;

const SideMenu = styled.div`
  width: 380px;
  flex-shrink: 0;
  position: sticky;
  top: 180px;
  align-self: flex-start;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: auto;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: #f8fafc; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 2px; }
`;

const MenuList = styled.div`
  display: flex;
  flex-direction: column;
`;

const MainMenuItem = styled.div<{ $active: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 13px 20px;
  cursor: pointer;
  border-bottom: 1px solid #f1f5f9;
  background: ${(p) => (p.$active ? "#f0f6fd" : "#fff")};
  transition: background 0.15s;

  &:last-child { border-bottom: none; }

  &:hover {
    background: ${(p) => (p.$active ? "#eff6ff" : "#f8fafc")};
  }
`;

const MenuItemLabel = styled.span<{ $active: boolean }>`
  font-size: 14px;
  font-weight: ${(p) => (p.$active ? 600 : 400)};
  color: ${(p) => (p.$active ? "#1e4d8c" : "#374151")};
  line-height: 1.5;
  flex: 1;
  padding-right: 8px;
`;

const CountBadge = styled.span<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 28px;
  height: 22px;
  padding: 0 8px;
  border-radius: 2px;
  background: ${(p) => (p.$active ? "#ffffff" : "#f6f6f6")};
  color: ${(p) => (p.$active ? "#5386ca" : "#64748b")};
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
  transition: all 0.15s;

  .cnt {
    display: inline-flex;
    align-items: center;
    gap: 3px;
  }
  b {
    font-weight: 700;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  min-height: 600px;
  min-width: 0;
  padding-top: 4px;
`;

const SubSection = styled.div`
  margin-bottom: 6px;
  overflow: hidden;
  background: #fff;
`;

const SubHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 18px;
  cursor: pointer;
  background: #fff;
  transition: background 0.15s;
  user-select: text; /* 텍스트 드래그 복사 허용 (클릭 펼침은 그대로 동작) */
  gap: 10px;

  &:hover { background: #f8fafc; }
`;

const SubHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
`;

const ChevronIcon = styled.span<{ $expanded: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-size: 12px;
  transition: transform 0.2s ease;
  transform: ${(p) => (p.$expanded ? "rotate(0deg)" : "rotate(-90deg)")};
  flex-shrink: 0;
`;

const SubTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  flex: 1;
  min-width: 0;
`;

const SubCount = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  background: #f6f6f6;
  padding: 2px 8px;
  border-radius: 2px;
  flex-shrink: 0;

  .cnt {
    display: inline-flex;
    align-items: center;
    gap: 3px;
  }
  b {
    font-weight: 700;
  }
`;

const PostListWrapper = styled.div<{ $expanded: boolean; $count: number }>`
  max-height: ${(p) => (p.$expanded ? `${p.$count * 44 + 20}px` : "0px")};
  overflow: hidden;
  transition: max-height 0.25s ease;
`;

const PostList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 4px 18px 14px 46px;
  gap: 2px;
  border-top: 1px solid #f1f5f9;
`;

const PostLink = styled.div`
  color: #374151;
  font-size: 14px;
  cursor: pointer;
  padding: 7px 4px;
  border-radius: 4px;
  line-height: 1.5;
  transition: color 0.15s, background 0.15s;
  display: flex;
  align-items: center;

  &::before {
    content: "–";
    margin-right: 10px;
    color: #94a3b8;
    flex-shrink: 0;
  }

  &:hover {
    color: #1e4d8c;
    background: #f8fcff;
    padding-left: 8px;
  }
`;

const StAddButton = styled.button`
  background: transparent;
  border: 1px solid #d9d9d9;
  border-radius: 14px;
  padding: 4px 10px;
  cursor: pointer;
  font-size: 12px;
  color: #888;
  display: flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  transition: all 0.2s;

  &:hover {
    color: #1890ff;
    border-color: #1890ff;
    background-color: #e6f7ff;
  }
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  width: 100%;
  flex: 1;
`;

export default Category;